import { translateText } from '@gm-commercial/translations';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useRef, useState } from 'react';
import MagnifyingGlassIcon from '../../assets/icons/search-sm.svg?react';
import { Flex } from '../Flex';
import {
  type SearchBarProps,
  SearchLabelType,
  SearchType,
} from './SearchBar.model';
import useStyles from './SearchBar.styles';

const SearchBar = ({
  searchInput,
  onSearch,
  searchTypes = [],
  withoutSearchTypeSelector,
  onClear,
  initialSearchType,
  dataTestId,
  isFullWidthSearchBar,
  useAltInputBackground = false,
  hideSearchByPrefix = false,
  placeholder,
  searchOnInputChange = false,
}: SearchBarProps): JSX.Element => {
  const { classes } = useStyles({
    isFullWidthSearchBar,
    useAltInputBackground,
  });
  const [searchType, setSearchType] = useState<SearchType>(
    initialSearchType || searchTypes[0] || SearchType.MULTIPLE_SEARCH
  );

  const [input, setInput] = useState<string>('');
  const committedSearchRef = useRef<string>(searchInput || '');

  const getLabel = useCallback(
    (searchType: SearchType, labelType: SearchLabelType) => {
      if (searchType === SearchType.MULTIPLE_SEARCH) {
        return translateText('common:search.search');
      }
      return translateText(`common:searchBy.${labelType}.${searchType}`);
    },
    []
  );

  useEffect(() => {
    setInput(searchInput || '');
    committedSearchRef.current = searchInput || '';
  }, [searchInput]);

  useEffect(() => {
    if (initialSearchType) {
      setSearchType(initialSearchType);
    }
  }, [initialSearchType]);

  return (
    <Flex center className={classes.searchContainer} data-heap-redact-text>
      {!withoutSearchTypeSelector && searchTypes.length > 0 && (
        <FormControl size="small">
          <Select
            disabled={searchTypes.length === 1}
            id="search-dropdown"
            IconComponent={KeyboardArrowDownIcon}
            data-testid={dataTestId ?? 'search-dropdown'}
            autoWidth
            className={classes.searchDropdown}
            value={searchType}
            MenuProps={{
              PaperProps: {
                className: 'bdSelectDropDownPaper',
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            onChange={(e) => {
              const newSearchType = e.target.value as SearchType;
              if (input === committedSearchRef.current && input !== '') {
                setSearchType(newSearchType);
                setInput('');
                committedSearchRef.current = '';
                onSearch(searchType, '');
              }
              setSearchType(newSearchType);
            }}
            renderValue={(selected) => (
              <>{getLabel(selected, SearchLabelType.SHORT)}</>
            )}
          >
            {searchTypes.map((value) => (
              <MenuItem
                key={String(value)}
                value={value as string}
                data-testid={`search-${String(value)}-list-item`}
              >
                {getLabel(
                  value,
                  hideSearchByPrefix
                    ? SearchLabelType.SHORT
                    : SearchLabelType.LONG
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box flexGrow="1">
        <FormControl fullWidth>
          <TextField
            id="search-field"
            data-testid="search-field"
            data-heap-redact-text
            value={input}
            className={classes.textField}
            onChange={(e) => {
              const newValue = e.target.value || '';
              setInput(newValue);

              if (searchOnInputChange) {
                const trimmedInput = newValue.trim();
                if (trimmedInput === committedSearchRef.current) {
                  return;
                }
                committedSearchRef.current = trimmedInput;
                onSearch(searchType, trimmedInput);
                return;
              }

              if (e.target.value === '' && committedSearchRef.current) {
                committedSearchRef.current = '';
                onSearch(searchType, '');
              }
            }}
            placeholder={
              placeholder || getLabel(searchType, SearchLabelType.LONG)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon aria-hidden="true" />
                </InputAdornment>
              ),
            }}
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input) {
                const trimmedInput = input.trim();
                if (
                  trimmedInput &&
                  trimmedInput !== committedSearchRef.current
                ) {
                  committedSearchRef.current = trimmedInput;
                  onSearch(searchType, trimmedInput);
                }
              }
            }}
          />
          {input !== '' && (
            <IconButton
              id="clear-search"
              data-testid="clear-search"
              aria-label={translateText('common:close')}
              size="large"
              className={classes.closeIconContainer}
              onClick={() => {
                if (committedSearchRef.current || searchOnInputChange) {
                  committedSearchRef.current = '';
                  onSearch(searchType, '');
                  onClear?.();
                }
                setInput('');
              }}
              disabled={!input}
            >
              <CloseTwoToneIcon className={classes.closeIcon} />
            </IconButton>
          )}
        </FormControl>
      </Box>
    </Flex>
  );
};

export default SearchBar;
